/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {
  useState, useContext, useRef, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import {
  Modal, Box, Button, TextField, Select, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
} from '@mui/material';
import { CSVLink } from 'react-csv';
import UploadIcon from '../../images/upload.png';
import FileIcon from '../../images/csvfile.png';
import RemoveIcon from '../../images/remove.png';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { UserContext } from '../../contexts/User';
import { NotificationContext } from '../../contexts/Notification';

const Description = styled.p`
    font-size: ${(props) => `${props.fontSize}px`};
    color: ${(props) => props.color};
    font-weight: ${(props) => props.fontWeight};
    margin-top: ${(props) => `${props.marginTop}px`};
`;

const StyledBlock = styled.div`
  border: 2px dashed #ccc;
  border-radius: 4px;
  display: flex;
  padding: 0px 15px;
  align-items: center;
  justify-content: ${(props) => props.justifyContent};
  cursor: pointer;
  margin-top: 20px;
  height: 48px;
`;

const accountTypes = [
  'Workspace owner',
  'Workspace admin',
  'Team member',
];

// Sample data for CSV template
const sampleData = [
  {
    'First Name': 'John',
    'Last Name': 'Doe',
    'Email Address': 'john.doe@example.com',
    'Account Type': 'Team member',
    'Reports To': 'jane.smith@example.com',
    Department: 'Engineering',
    Country: 'United States',
    Designation: 'Software Developer',
    'Employment Start Date': '2024-01-15',
    Status: 'Active',
  },
  {
    'First Name': 'Jane',
    'Last Name': 'Smith',
    'Email Address': 'jane.smith@example.com',
    'Account Type': 'Workspace admin',
    'Reports To': 'admin@example.com',
    Department: 'Engineering',
    Country: 'United States',
    Designation: 'Team Lead',
    'Employment Start Date': '2024-01-01',
    Status: 'Active',
  },
];

const csvHeaders = [
  { label: 'First Name', key: 'First Name' },
  { label: 'Last Name', key: 'Last Name' },
  { label: 'Email Address', key: 'Email Address' },
  { label: 'Account Type', key: 'Account Type' },
  { label: 'Reports To', key: 'Reports To' },
  { label: 'Department', key: 'Department' },
  { label: 'Country', key: 'Country' },
  { label: 'Designation', key: 'Designation' },
  { label: 'Employment Start Date', key: 'Employment Start Date' },
  { label: 'Status', key: 'Status' },
];

function BulkImportMembersModal({
  closeModal, getMembers,
}) {
  const { activeWorkspace } = useContext(UserContext);
  const { displayNotification } = useContext(NotificationContext);
  const [file, setFile] = useState();
  const [importedUsers, setImportedUsers] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [apiErrors, setApiErrors] = useState([]); // Track API errors for display
  const [importStats, setImportStats] = useState(null); // Track import statistics
  const [existingMembers, setExistingMembers] = useState([]); // Track existing workspace members
  const fileInputRef = useRef(null);
  const fileReader = new FileReader();

  const handleFileInputClick = () => {
    fileInputRef.current.click();
  };

  // Fetch existing workspace members for validation
  const fetchExistingMembers = async () => {
    if (activeWorkspace) {
      try {
        const response = await sendRequest({
          method: 'get',
          url: API.WORKSPACE.members(activeWorkspace.id),
        });
        if (response.success && response.data?.members) {
          setExistingMembers(response.data.members);
        }
      } catch (error) {
        console.error('Error fetching existing members:', error);
      }
    }
  };

  // Fetch existing members when component mounts
  useEffect(() => {
    fetchExistingMembers();
  }, [activeWorkspace]);

  const validateUserData = (users) => {
    const errors = {};
    // Critical fields required by API
    const criticalFields = ['email', 'accountType'];
    // Recommended fields for better user experience
    const recommendedFields = ['firstName', 'lastName'];
    const emailMap = new Map(); // Track emails for duplicate detection

    // Create set of existing member emails for quick lookup
    const existingMemberEmails = new Set(
      existingMembers.map((member) => member.user?.email?.toLowerCase()).filter(Boolean),
    );

    users.forEach((user, index) => {
      const userErrors = {};

      // Check critical fields (these will block import)
      criticalFields.forEach((field) => {
        if (!user[field] || user[field].trim() === '') {
          userErrors[field] = 'Required';
        }
      });

      // Check recommended fields (these will show warnings but not block import)
      recommendedFields.forEach((field) => {
        if (!user[field] || user[field].trim() === '') {
          userErrors[field] = 'Recommended for better user identification';
        }
      });

      // Email validation
      if (user.email) {
        const email = user.email.trim().toLowerCase();

        // Check if user already exists in workspace
        if (existingMemberEmails.has(email)) {
          userErrors.email = 'User already exists in this workspace';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(user.email)) {
          userErrors.email = 'Invalid email format';
        } else {
          // Check for duplicate emails in import batch
          // eslint-disable-next-line no-lonely-if
          if (emailMap.has(email)) {
            const firstOccurrence = emailMap.get(email);
            userErrors.email = `Duplicate email (also found in row ${firstOccurrence + 1})`;

            // Also mark the first occurrence as having a duplicate
            if (!errors[firstOccurrence]) {
              errors[firstOccurrence] = {};
            }
            errors[firstOccurrence].email = `Duplicate email (also found in row ${index + 1})`;
          } else {
            emailMap.set(email, index);
          }
        }
      }

      // Account type validation
      if (user.accountType && !accountTypes.includes(user.accountType)) {
        userErrors.accountType = 'Invalid account type';
      }

      if (Object.keys(userErrors).length > 0) {
        errors[index] = userErrors;
      }
    });

    return errors;
  };

  // Check if there are any critical errors that should block import
  // eslint-disable-next-line max-len
  const hasCriticalErrors = (errors) => Object.values(errors).some((userErrors) => userErrors.email?.includes('Required')
      || userErrors.email?.includes('Invalid')
      || userErrors.email?.includes('Duplicate')
      || userErrors.email?.includes('already exists')
      || userErrors.accountType?.includes('Required')
      || userErrors.accountType?.includes('Invalid'));

  const csvFileToArray = (string) => {
    const newString = string.replaceAll('\r', '');
    const csvHeader = newString.slice(0, newString.indexOf('\n')).split(',');
    const csvRows = newString.slice(newString.indexOf('\n') + 1).split('\n');

    const users = csvRows.map((row) => {
      const values = row.split(',');
      const user = {};

      csvHeader.forEach((header, index) => {
        const cleanHeader = header.replaceAll('"', '').trim();
        const value = values[index] ? values[index].replaceAll('"', '').trim() : '';

        // Map CSV headers to our internal field names
        switch (cleanHeader) {
          case 'First Name':
            user.firstName = value;
            break;
          case 'Last Name':
            user.lastName = value;
            break;
          case 'Email Address':
            user.email = value;
            break;
          case 'Account Type':
            user.accountType = value;
            break;
          case 'Reports To':
            user.reportsTo = value;
            break;
          case 'Department':
            user.department = value;
            break;
          case 'Country':
            user.country = value || 'United States'; // Default to United States
            break;
          case 'Designation':
            user.designation = value;
            break;
          case 'Employment Start Date':
            user.employmentStartDate = value;
            break;
          case 'Status':
            user.status = value || 'Active';
            break;
          default:
            break;
        }
      });

      return user;
    }).filter((user) => user.firstName || user.lastName || user.email); // Filter out empty rows

    setImportedUsers(users);
    const errors = validateUserData(users);
    setValidationErrors(errors);
    // Clear previous import stats and API errors when parsing new data
    setApiErrors([]);
    setImportStats(null);
  };

  // Re-validate when existing members are loaded
  useEffect(() => {
    if (existingMembers.length > 0 && importedUsers.length > 0) {
      const errors = validateUserData(importedUsers);
      setValidationErrors(errors);
    }
  }, [existingMembers]);

  const handleImport = () => {
    fileReader.onload = (event) => {
      const csvOutput = event.target.result;
      csvFileToArray(csvOutput);
    };

    fileReader.readAsText(file);
  };

  const handleUserChange = (index, field, value) => {
    const updatedUsers = [...importedUsers];
    updatedUsers[index][field] = value;

    // If country is empty, default to United States
    if (field === 'country' && !value) {
      updatedUsers[index][field] = 'United States';
    }

    setImportedUsers(updatedUsers);

    // Re-validate after change
    const errors = validateUserData(updatedUsers);
    setValidationErrors(errors);

    // Clear API errors and import stats when user makes changes
    if (apiErrors.length > 0) {
      setApiErrors([]);
    }
    if (importStats) {
      setImportStats(null);
    }
  };

  const handleConfirm = async () => {
    if (activeWorkspace && importedUsers.length > 0) {
      // Final validation
      const errors = validateUserData(importedUsers);
      if (hasCriticalErrors(errors)) {
        displayNotification({
          message: 'Please fix critical validation errors (email and account type issues) before proceeding',
          type: 'error',
        });
        return;
      }

      setApiErrors([]); // Clear previous API errors
      setImportStats(null); // Clear previous import stats

      try {
        let successCount = 0;
        let errorCount = 0;
        const errorMessages = [];
        const totalUsers = importedUsers.length;

        // First, get existing workspace members to check for duplicates
        const existingMembersResponse = await sendRequest({
          method: 'get',
          url: API.WORKSPACE.members(activeWorkspace.id),
        });

        const existingMembers = existingMembersResponse.success ? existingMembersResponse.data?.members || [] : [];
        const existingMemberEmails = new Set(existingMembers.map((member) => member.user?.email?.toLowerCase()));

        // Create a map of emails to member IDs for reporting manager lookup
        const emailToMemberMap = {};
        existingMembers.forEach((member) => {
          if (member.user?.email) {
            emailToMemberMap[member.user.email.toLowerCase()] = member.id;
          }
        });

        // Process each user for import
        // eslint-disable-next-line no-restricted-syntax
        for (const user of importedUsers) {
          try {
            const userEmail = user.email.toLowerCase();
            const userIdentifier = user.firstName && user.lastName
              ? `${user.firstName} ${user.lastName}`
              : user.email;

            // Check if user is already a member of this workspace
            if (existingMemberEmails.has(userEmail)) {
              errorCount += 1;
              errorMessages.push(`${userIdentifier} - User already exists in this workspace`);
              // eslint-disable-next-line no-continue
              continue;
            }

            // Prepare member data
            const memberData = {
              email: user.email,
              accountType: user.accountType,
              department: user.department || '',
              designation: user.designation || '',
            };

            // Handle reporting manager lookup
            if (user.reportsTo) {
              const reportingEmail = user.reportsTo.toLowerCase();
              if (emailToMemberMap[reportingEmail]) {
                memberData.reportingManager = emailToMemberMap[reportingEmail];
              } else {
                // Check if reporting manager is in the import batch
                // eslint-disable-next-line max-len
                const reportingManagerInBatch = importedUsers.find((u) => u.email.toLowerCase() === reportingEmail);
                if (!reportingManagerInBatch) {
                  errorCount += 1;
                  // eslint-disable-next-line max-len
                  errorMessages.push(`${userIdentifier} - Reporting manager "${user.reportsTo}" not found in workspace or import batch`);
                  // eslint-disable-next-line no-continue
                  continue;
                }
                // If reporting manager is in batch, we'll need to process them first
                // For now, we'll skip the reporting manager assignment and warn the user
                memberData.reportingManager = '';
              }
            } else {
              memberData.reportingManager = '';
            }

            // eslint-disable-next-line no-await-in-loop
            const response = await sendRequest({
              method: 'post',
              url: API.WORKSPACE.member(activeWorkspace.id),
              data: memberData,
            });

            if (response.success) {
              successCount += 1;
              // Add the new member to our tracking maps for subsequent imports
              const newMemberEmail = user.email.toLowerCase();
              existingMemberEmails.add(newMemberEmail);
              if (response.data?.user?.id) {
                emailToMemberMap[newMemberEmail] = response.data.user.id;
              }
            } else {
              errorCount += 1;
              // Collect specific error messages from the API
              if (response.data && typeof response.data === 'object') {
                const errorFields = Object.entries(response.data)
                  .map(([field, message]) => `${field}: ${message}`)
                  .join(', ');
                errorMessages.push(`${userIdentifier} - ${errorFields}`);
              } else {
                errorMessages.push(`${userIdentifier} - ${response.message || 'Unknown error'}`);
              }
            }
          } catch (error) {
            errorCount += 1;
            const userIdentifier = user.firstName && user.lastName
              ? `${user.firstName} ${user.lastName}`
              : user.email;
            errorMessages.push(`${userIdentifier} - ${error.message || 'Network error'}`);
          }
        }

        // Calculate and store import statistics
        const stats = {
          total: totalUsers,
          successful: successCount,
          failed: errorCount,
          successRate: Math.round((successCount / totalUsers) * 100),
        };
        setImportStats(stats);

        // Store API errors for display in the modal
        if (errorMessages.length > 0) {
          setApiErrors(errorMessages);
        }

        // Display results with detailed statistics
        if (successCount > 0 && errorCount === 0) {
          displayNotification({
            message: `🎉 Successfully imported all ${successCount} users`,
            type: 'success',
          });
          closeModal();
          getMembers();
        } else if (successCount > 0 && errorCount > 0) {
          displayNotification({
            // eslint-disable-next-line max-len
            message: `📊 Import completed: ${successCount} users added successfully, ${errorCount} failed. See details below.`,
            type: 'warning',
          });
          // Don't close modal so user can see the detailed statistics and errors
        } else {
          // All failed - show error with statistics
          displayNotification({
            message: `❌ Import failed: 0 of ${totalUsers} users were added. See error details below.`,
            type: 'error',
          });
          // Don't close modal so user can see the error details
        }
      } catch (error) {
        displayNotification({
          message: `Error importing users: ${error.message || 'Unknown error'}`,
          type: 'error',
        });
      }
    }
  };

  const isFormValid = importedUsers.length > 0 && !hasCriticalErrors(validationErrors);

  return (
    <Modal open onClose={closeModal} className="flex-row align-c jstfy-c">
      <Box
        sx={{
          width: '90%',
          maxWidth: 1200,
          maxHeight: '90vh',
          overflow: 'auto',
          padding: '32px',
          background: '#fff',
          borderRadius: 8,
        }}
      >
        <h5 className="m-b16 text-600" data-testid="modal-title">Import Users</h5>

        <Description fontSize={14} color="#6B7280" fontWeight={400} marginTop={0}>
          Download the sample file to see the expected format, then upload your file with user data.
        </Description>

        <div style={{ marginTop: 16, marginBottom: 24 }}>
          <CSVLink
            data={sampleData}
            headers={csvHeaders}
            filename="bulk_users_template.csv"
            style={{
              color: '#4469A1',
              textDecoration: 'underline',
              fontSize: 14,
            }}
          >
            Download Sample File
          </CSVLink>
        </div>

        <form>
          {file ? (
            <StyledBlock justifyContent="space-between">
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <img src={FileIcon} style={{ width: '18px', marginRight: '8px' }} alt="File" />
                <span>{file.name}</span>
              </div>
              <img
                src={RemoveIcon}
                style={{ width: '18px', marginRight: '4px', cursor: 'pointer' }}
                alt="Remove"
                onClick={() => {
                  setFile();
                  setImportedUsers([]);
                  setValidationErrors({});
                }}
              />
            </StyledBlock>
          ) : (
            <StyledBlock justifyContent="center" onClick={handleFileInputClick}>
              <img src={UploadIcon} style={{ width: '14px', marginRight: '4px' }} alt="Upload" />
              <span>Drop your .csv, .xls or .xlsx file here or </span>
              <span
                style={{
                  textDecoration: 'underline',
                  textDecorationColor: '#4469A1',
                  color: '#4469A1',
                  marginLeft: '4px',
                }}
              >
                Browse
              </span>
              <input
                ref={fileInputRef}
                type="file"
                id="csvFileInput"
                style={{ display: 'none' }}
                onChange={(e) => { setFile(e.target.files[0]); }}
                accept=".csv,.xls,.xlsx"
              />
            </StyledBlock>
          )}
        </form>

        {file && !importedUsers.length && (
          <div style={{ marginTop: 16 }}>
            <Button
              variant="outlined"
              onClick={handleImport}
              disabled={!file}
            >
              Parse File
            </Button>
          </div>
        )}

        {importedUsers.length > 0 && (
          <div style={{ marginTop: 24 }}>
            {/* Error Summary Section */}
            {Object.keys(validationErrors).length > 0 && (
              <div style={{
                marginBottom: 16,
                padding: 16,
                backgroundColor: '#fff2f0',
                border: '1px solid #ffccc7',
                borderRadius: 8,
              }}
              >
                <h6 style={{ color: '#cf1322', marginBottom: 12, fontWeight: 600 }}>
                  ⚠️ Validation Errors Found (
                  {Object.keys(validationErrors).length}
                  {' '}
                  users with issues)
                </h6>
                <div style={{ fontSize: '14px', color: '#8c8c8c' }}>
                  Please fix the following issues before importing:
                </div>
                <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                  {Object.entries(validationErrors).map(([index, userErrors]) => {
                    const userNumber = parseInt(index, 10) + 1;
                    const user = importedUsers[index];
                    const userName = user.firstName && user.lastName
                      ? `${user.firstName} ${user.lastName}`
                      : user.email || `Row ${userNumber}`;

                    return (
                      <li key={index} style={{ color: '#cf1322', marginBottom: 4 }}>
                        <strong>
                          {userName}
                          {' '}
                          (Row
                          {' '}
                          {userNumber}
                          ):
                        </strong>
                        {' '}
                        {Object.entries(userErrors).map(([field, error]) => {
                          const fieldLabel = {
                            firstName: 'First Name',
                            lastName: 'Last Name',
                            email: 'Email',
                            accountType: 'Account Type',
                            reportsTo: 'Reports To',
                            employmentStartDate: 'Employment Start Date',
                          }[field] || field;
                          return `${fieldLabel} - ${error}`;
                        }).join(', ')}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* API Error Summary Section */}
            {apiErrors.length > 0 && (
              <div style={{
                marginBottom: 16,
                padding: 16,
                backgroundColor: '#fff1f0',
                border: '1px solid #ffa39e',
                borderRadius: 8,
              }}
              >
                <h6 style={{ color: '#cf1322', marginBottom: 12, fontWeight: 600 }}>
                  🚫 API Import Errors (
                  {apiErrors.length}
                  {' '}
                  errors)
                </h6>
                <div style={{ fontSize: '14px', color: '#8c8c8c', marginBottom: 8 }}>
                  The following users could not be imported due to server errors:
                </div>
                <ul style={{
                  marginTop: 8, marginBottom: 0, paddingLeft: 20, maxHeight: 150, overflow: 'auto',
                }}
                >
                  {apiErrors.map((error, index) => (
                    <li key={index} style={{ color: '#cf1322', marginBottom: 4, fontSize: '13px' }}>
                      {error}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Import Statistics Section */}
            {importStats && (
              <div style={{
                marginBottom: 16,
                padding: 16,
                backgroundColor: importStats.failed === 0 ? '#f6ffed' : '#fffbe6',
                border: `1px solid ${importStats.failed === 0 ? '#b7eb8f' : '#ffe58f'}`,
                borderRadius: 8,
              }}
              >
                <h6 style={{
                  color: importStats.failed === 0 ? '#389e0d' : '#d46b08',
                  marginBottom: 12,
                  fontWeight: 600,
                }}
                >
                  📊 Import Statistics
                </h6>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                  gap: 12,
                  fontSize: '14px',
                }}
                >
                  <div style={{
                    textAlign: 'center', padding: 8, backgroundColor: '#fff', borderRadius: 4,
                  }}
                  >
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1890ff' }}>
                      {importStats.total}
                    </div>
                    <div style={{ color: '#8c8c8c' }}>Total Rows</div>
                  </div>
                  <div style={{
                    textAlign: 'center', padding: 8, backgroundColor: '#fff', borderRadius: 4,
                  }}
                  >
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#52c41a' }}>
                      {importStats.successful}
                    </div>
                    <div style={{ color: '#8c8c8c' }}>Successfully Added</div>
                  </div>
                  <div style={{
                    textAlign: 'center', padding: 8, backgroundColor: '#fff', borderRadius: 4,
                  }}
                  >
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#ff4d4f' }}>
                      {importStats.failed}
                    </div>
                    <div style={{ color: '#8c8c8c' }}>Failed</div>
                  </div>
                  <div style={{
                    textAlign: 'center', padding: 8, backgroundColor: '#fff', borderRadius: 4,
                  }}
                  >
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#722ed1' }}>
                      {importStats.successRate}
                      %
                    </div>
                    <div style={{ color: '#8c8c8c' }}>Success Rate</div>
                  </div>
                </div>
                {importStats.successful > 0 && (
                  <div style={{
                    marginTop: 12,
                    padding: 8,
                    backgroundColor: '#f6ffed',
                    borderRadius: 4,
                    fontSize: '14px',
                    color: '#389e0d',
                    fontWeight: 500,
                  }}
                  >
                    ✅
                    {' '}
                    {importStats.successful}
                    {' '}
                    user
                    {importStats.successful !== 1 ? 's' : ''}
                    {' '}
                    {importStats.successful !== 1 ? 'have' : 'has'}
                    {' '}
                    been successfully added to the workspace
                  </div>
                )}
              </div>
            )}

            <h6 style={{ marginBottom: 16 }}>
              Preview and Edit Users (
              {importedUsers.length}
              {' '}
              users)
              {Object.keys(validationErrors).length === 0 && (
                <span style={{ color: '#52c41a', fontSize: '14px', marginLeft: 8 }}>
                  ✓ All users are valid
                </span>
              )}
            </h6>
            <TableContainer component={Paper} style={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>First Name</TableCell>
                    <TableCell>Last Name</TableCell>
                    <TableCell>Email*</TableCell>
                    <TableCell>Account Type*</TableCell>
                    <TableCell>Reports To</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell>Country</TableCell>
                    <TableCell>Designation</TableCell>
                    <TableCell>Start Date</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {importedUsers.map((user, index) => {
                    const hasErrors = validationErrors[index];
                    return (
                      <TableRow
                        key={index}
                        style={{
                          backgroundColor: hasErrors ? '#fff2f0' : 'transparent',
                          border: hasErrors ? '1px solid #ffccc7' : 'none',
                        }}
                      >
                        <TableCell>
                          <TextField
                            size="small"
                            value={user.firstName || ''}
                            onChange={(e) => handleUserChange(index, 'firstName', e.target.value)}
                            error={!!(validationErrors[index]?.firstName)}
                            helperText={validationErrors[index]?.firstName}
                            style={{ width: '100%', minWidth: 100 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={user.lastName || ''}
                            onChange={(e) => handleUserChange(index, 'lastName', e.target.value)}
                            error={!!(validationErrors[index]?.lastName)}
                            helperText={validationErrors[index]?.lastName}
                            style={{ width: '100%', minWidth: 100 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={user.email || ''}
                            onChange={(e) => handleUserChange(index, 'email', e.target.value)}
                            error={!!(validationErrors[index]?.email)}
                            helperText={validationErrors[index]?.email}
                            style={{ width: '100%', minWidth: 150 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Select
                            size="small"
                            value={user.accountType || ''}
                            onChange={(e) => handleUserChange(index, 'accountType', e.target.value)}
                            error={!!(validationErrors[index]?.accountType)}
                            style={{ minWidth: 140, width: '100%' }}
                            displayEmpty
                          >
                            <MenuItem value="" disabled>
                              <em>Select type...</em>
                            </MenuItem>
                            {accountTypes.map((type) => (
                              <MenuItem key={type} value={type}>
                                {type}
                              </MenuItem>
                            ))}
                          </Select>
                          {validationErrors[index]?.accountType && (
                            <div style={{
                              fontSize: '12px',
                              color: '#d32f2f',
                              marginTop: '4px',
                            }}
                            >
                              {validationErrors[index].accountType}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={user.reportsTo || ''}
                            onChange={(e) => handleUserChange(index, 'reportsTo', e.target.value)}
                            error={!!(validationErrors[index]?.reportsTo)}
                            helperText={validationErrors[index]?.reportsTo}
                            style={{ width: '100%', minWidth: 150 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={user.department || ''}
                            onChange={(e) => handleUserChange(index, 'department', e.target.value)}
                            style={{ width: '100%', minWidth: 100 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={user.country || 'United States'}
                            onChange={(e) => handleUserChange(index, 'country', e.target.value)}
                            style={{ width: '100%', minWidth: 100 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={user.designation || ''}
                            onChange={(e) => handleUserChange(index, 'designation', e.target.value)}
                            style={{ width: '100%', minWidth: 120 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="date"
                            value={user.employmentStartDate || ''}
                            onChange={(e) => handleUserChange(index, 'employmentStartDate', e.target.value)}
                            error={!!(validationErrors[index]?.employmentStartDate)}
                            helperText={validationErrors[index]?.employmentStartDate}
                            style={{ width: '100%', minWidth: 140 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={user.status || 'Active'}
                            onChange={(e) => handleUserChange(index, 'status', e.target.value)}
                            style={{ width: '100%', minWidth: 80 }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        )}

        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24,
        }}
        >
          <Button
            variant="outlined"
            onClick={closeModal}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={!isFormValid}
          >
            Import Users (
            {importedUsers.length}
            )
          </Button>
        </div>
      </Box>
    </Modal>
  );
}

BulkImportMembersModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  getMembers: PropTypes.func.isRequired,
};

export default BulkImportMembersModal;
